import { Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Table } from '@/components/ui';
import { formatChangePct, formatMarketCap, formatPrice } from '@/lib/format';
import type { SortCol, SortDir } from '@/lib/ui-store';
import type { TickerRow } from '@/hooks/useTickers';
import { BuzzBadge } from '@/components/shared/BuzzBadge';

interface StockTableProps {
  tickers: TickerRow[];
  sortCol: SortCol;
  sortDir: SortDir;
  onSort: (col: SortCol) => void;
  onSelect?: (ticker: TickerRow) => void;
}

function SortIcon({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol; sortDir: SortDir }) {
  if (col !== sortCol) return <ArrowUpDown size={13} opacity={0.35} aria-hidden />;
  return sortDir === 'asc' ? (
    <ArrowUp size={13} aria-hidden />
  ) : (
    <ArrowDown size={13} aria-hidden />
  );
}

function SortableHeader({
  col,
  label,
  sortCol,
  sortDir,
  onSort,
  ta,
}: {
  col: SortCol;
  label: string;
  sortCol: SortCol;
  sortDir: SortDir;
  onSort: (col: SortCol) => void;
  ta?: 'left' | 'right' | 'center';
}) {
  const active = col === sortCol;
  return (
    <Table.Th ta={ta}>
      <UnstyledButton
        onClick={() => onSort(col)}
        style={{ width: '100%' }}
        aria-label={`Sort by ${label}`}
      >
        <Group
          gap={4}
          wrap="nowrap"
          justify={ta === 'right' ? 'flex-end' : 'flex-start'}
          style={{ fontWeight: active ? 700 : 600 }}
        >
          {label}
          <SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

function changeColour(value: number | null): string {
  if (value === null || value === 0) return 'inherit';
  return value > 0 ? 'teal' : 'red';
}

export function StockTable({ tickers, sortCol, sortDir, onSort, onSelect }: StockTableProps) {
  return (
    <Table.ScrollContainer minWidth={640}>
      <Table striped highlightOnHover withTableBorder verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <SortableHeader col="symbol" label="Symbol" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
            <SortableHeader col="name" label="Company" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
            <SortableHeader col="last_price" label="Price" sortCol={sortCol} sortDir={sortDir} onSort={onSort} ta="right" />
            <SortableHeader col="last_price_change_pct" label="Change" sortCol={sortCol} sortDir={sortDir} onSort={onSort} ta="right" />
            <SortableHeader col="market_cap" label="Market cap" sortCol={sortCol} sortDir={sortDir} onSort={onSort} ta="right" />
            <SortableHeader col="buzz_score" label="Buzz" sortCol={sortCol} sortDir={sortDir} onSort={onSort} ta="right" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tickers.map((t) => (
            <Table.Tr
              key={t.id}
              onClick={() => onSelect?.(t)}
              style={onSelect ? { cursor: 'pointer' } : undefined}
            >
              <Table.Td>
                <Text fw={600}>{t.symbol}</Text>
              </Table.Td>
              <Table.Td>
                <Stack gap={0}>
                  <Text>{t.name}</Text>
                  {t.industry && (
                    <Text size="xs" c="dimmed">
                      {t.industry}
                    </Text>
                  )}
                </Stack>
              </Table.Td>
              <Table.Td ta="right">{formatPrice(t.last_price)}</Table.Td>
              <Table.Td ta="right">
                <Text c={changeColour(t.last_price_change_pct)} component="span">
                  {formatChangePct(t.last_price_change_pct)}
                </Text>
              </Table.Td>
              <Table.Td ta="right">{formatMarketCap(t.market_cap)}</Table.Td>
              <Table.Td ta="right">
                <Group justify="flex-end">
                  <BuzzBadge score={t.buzz_score} />
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
